import type { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { compositionUrl } from '@seen/core/models/compositionUrl'
import type { ParticipantNote } from '@seen/core/models/participantNote'
import { compact, orderBy } from 'lodash-es'

/* How many signed notes the feed shows. The query asks for one more than this
 * so the feed can tell "that is all of them" from "there are more" — without
 * the extra row a full page and an exactly-full result look identical. */
export const NOTE_LIMIT = 100

/* The entries that sit on the timeline and therefore sort by time. Kept as
 * data rather than rendered nodes so the merge, the ordering and the truncation
 * can be tested without a DOM, as `buildCompositionActivity` and the day-center
 * schedule feed are. */
export type TimedInterventionActivityEntry =
  | { kind: 'comment'; time: string | undefined; comment: CommentCommunication }
  | {
      kind: 'note'
      time: string | undefined
      note: ParticipantNote
      href: string
    }
  | { kind: 'created'; time: string | undefined }

export type InterventionActivityEntry =
  | TimedInterventionActivityEntry
  /* Not a timeline event but a statement about the list: older notes exist and
   * are not on screen. Emitted last so it sits at the oldest end, which is the
   * end the missing notes fall off. */
  | { kind: 'notes-truncated'; shown: number }

/* Everything that has happened to an intervention, newest first: the comments
 * written on it, the signed notes that mention it, and the fact of its
 * creation. Newest-first matches the composition and appointment feeds, so the
 * creation entry lands at the bottom where it belongs.
 *
 * `notes` is expected to hold up to `noteLimit + 1` rows. The extra one is
 * never rendered; it only tells the feed that it is showing a truncated list,
 * so a reader can tell "no more notes" from "more notes, not shown". */
export function buildInterventionActivity({
  comments,
  notes,
  created,
  noteLimit = NOTE_LIMIT,
}: {
  comments: CommentCommunication[]
  notes: ParticipantNote[]
  created: string | undefined
  noteLimit?: number
}): InterventionActivityEntry[] {
  const truncated = notes.length > noteLimit

  const noteEntries = compact(
    notes
      .slice(0, noteLimit)
      .map((note): TimedInterventionActivityEntry | null => {
        const href = compositionUrl(note.toFhirResource())
        /* A composition the URL builder does not recognise has nowhere to send
         * the reader, and a row that goes nowhere is worse than no row. */
        return href ? { kind: 'note', time: note.signedOn, note, href } : null
      })
  )

  const entries = orderBy(
    [
      ...comments.map((comment): TimedInterventionActivityEntry => ({
        kind: 'comment',
        time: comment.sent,
        comment,
      })),
      ...noteEntries,
      { kind: 'created' as const, time: created },
    ],
    (entry) => entry.time,
    'desc'
  )

  return truncated
    ? [
        ...entries,
        {
          kind: 'notes-truncated',
          shown: noteEntries.filter((e) => e.kind === 'note').length,
        },
      ]
    : entries
}
